/**
 * Intended route persistence helpers for post-login restoration.
 * Separated into its own module to break circular dependencies between
 * router and logoutService.
 */

import type {
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded
} from 'vue-router';

const INTENDED_ROUTE_KEY = 'intendedRoute';
const RESTORED_INTENDED_ROUTE_KEY = 'intendedRouteRestored';

/**
 * Save the route the user was trying to access before logout/login redirect.
 * Called during auth failures to preserve navigation context.
 */
export const saveIntendedRoute = (
  route: RouteLocationNormalizedLoaded | RouteLocationNormalized
): void => {
  // Don't save login or error routes
  if (route.name === 'login' || route.name === 'error') return;

  // Persist only non-sensitive route fields for post-login restoration.
  // Use route.path (no query string) to avoid storing sensitive data in sessionStorage.
  sessionStorage.setItem(
    INTENDED_ROUTE_KEY,
    JSON.stringify({
      path: route.path,
      name: route.name,
      params: route.params
    })
  );
};

/**
 * Mark that `path` was just restored from a saved intended route. If navigating
 * there immediately fails an auth check, callers use this to avoid re-saving it
 * and trapping the user in a login → restore → fail → logout loop. One-shot: the
 * mark self-clears once the user navigates to any other route (see authGuard),
 * so a genuine later failure on the same route is still saved normally.
 */
export const markIntendedRouteRestored = (path: string): void => {
  sessionStorage.setItem(RESTORED_INTENDED_ROUTE_KEY, path);
};

/**
 * True when `route` is the one just restored from a saved intended route.
 */
export const wasIntendedRouteRestored = (
  route: RouteLocationNormalizedLoaded | RouteLocationNormalized
): boolean =>
  sessionStorage.getItem(RESTORED_INTENDED_ROUTE_KEY) === route.path;

/**
 * Clear the restored-intended-route mark.
 */
export const clearIntendedRouteRestoredMark = (): void => {
  sessionStorage.removeItem(RESTORED_INTENDED_ROUTE_KEY);
};

/**
 * Retrieve and clear the intended route from storage.
 * Called during login flow to redirect user to their original destination.
 */
export const getAndClearIntendedRoute = () => {
  const savedRoute = sessionStorage.getItem(INTENDED_ROUTE_KEY);
  if (savedRoute) {
    sessionStorage.removeItem(INTENDED_ROUTE_KEY);
    try {
      return JSON.parse(savedRoute);
    } catch (e) {
      console.error('Failed to parse intended route:', e);
      return null;
    }
  }
  return null;
};
